// SPDX-License-Identifier: GPL-3.0-only
// Original behavior reconstructed by OpenSaga: bolttypes_lsw.inc, bolts.cpp,
// motion/move.cpp ShootCode, utilities.cpp SphereSphereOverlapScaleY and
// nutrig.cpp NuAtan2D. Original PC GlobalBoltType red record confirmed at file
// offset 0x3ef388, LEGOStarWarsSaga.exe SHA256
// 085688e73561c61f4619fcf1f3006c56425ca365e80ea5fc0175bab334cec671.
export const ORDINARY_RED_BOLT = Object.freeze({ name: 'red', typeId: 0, model: 'blasterbolt_red',
  speed: 4, lifetime: 2, radius: .125, collisionRadius: Math.fround(.125 * Math.fround(1 / 3)), scale: 1, growTime: Math.fround(.1), damage: 1 });

const validVector = vector => Array.isArray(vector) && vector.length === 3 && vector.every(Number.isFinite);
// All 513 generated values match the original ang[] table byte for byte.
const angleTable = Array.from({ length: 513 }, (_, i) => Math.round(Math.atan(i / 512) * 65536 / (2 * Math.PI)));
export function sourceAtan2(dx, dz) {
  dx = Math.fround(dx); dz = Math.fround(dz);
  if (dx === 0) return dz < 0 ? 0x8000 : 0;
  if (dz === 0) return dx < 0 ? 0xc000 : 0x4000;
  const x = Math.abs(dx), z = Math.abs(dz);
  const lookup = (a, b) => angleTable[Math.min(512, Math.trunc(Math.fround(Math.fround(a * 512) / b)))];
  const angle = x > z ? 0x4000 - lookup(z, x) : lookup(x, z);
  return dx < 0 ? dz < 0 ? angle + 0x8000 : -angle : dz < 0 ? 0x8000 - angle : angle;
}
const trig = (angle, cosine = false) => Math.fround(Math.sin(Math.fround((((angle + (cosine ? 0x4000 : 0)) >> 1) & 0x7fff) * Math.fround(2 * Math.PI / 32768))));
export function sourceBoltAnglesDirection(yaw, pitch) {
  if (!Number.isInteger(yaw) || !Number.isInteger(pitch)) throw new Error('Invalid original bolt angles');
  const cp = trig(pitch, true);
  return [Math.fround(trig(yaw) * cp), -trig(pitch), Math.fround(trig(yaw, true) * cp)];
}
export function sourceBoltDirection(origin, target) {
  if (!validVector(origin) || !validVector(target)) throw new Error('Invalid original bolt aim vector');
  const [dx, dy, dz] = target.map((v, i) => Math.fround(v - origin[i]));
  if (dx === 0 && dy === 0 && dz === 0) throw new Error('Coincident original bolt target');
  const yaw = sourceAtan2(dx, dz), pitch = -sourceAtan2(dy, Math.fround(Math.sqrt(Math.fround(dx * dx + dz * dz))));
  return sourceBoltAnglesDirection(yaw, pitch);
}

export function sourceBoltOverlap(collision, point, radius) {
  if (!collision || !validVector(collision.position) || !validVector(point) ||
      ![collision.radius, collision.halfHeight, radius].every(v => Number.isFinite(v) && v >= 0) || collision.halfHeight + radius <= 0)
    throw new Error('Invalid original projectile collision volume');
  const dx = point[0] - collision.position[0], dz = point[2] - collision.position[2];
  let dy = point[1] - collision.position[1];
  if (collision.radius !== collision.halfHeight) dy *= (radius + collision.radius) / (radius + collision.halfHeight);
  return dx * dx + dy * dy + dz * dz <= (radius + collision.radius) ** 2;
}

// Source Bolt_HitGameObject checks front, middle, back sphere points, rather
// than inventing a swept player-hit rule. Caller terrainSweep is the adapter
// for native raycast/scenery, returning null(clear), {fraction:[0,1]}(hit), or
// undefined(unknown). Unknown terrain halts the bolt and records a gap.
export function createProjectileSystem({ getShootOrigin, getAimPosition, terrainSweep,
  getTargets = () => [], getCollision, interceptCharacterHit = () => null, onDamage = () => {}, onSpawn = () => {}, onEnd = () => {} } = {}) {
  if (typeof getShootOrigin !== 'function' || typeof getAimPosition !== 'function' || typeof getCollision !== 'function')
    throw new Error('Original projectile adapters are required');
  const slots = Array(32).fill(null), shots = new Map(), report = { blocked: [], limitations: [
    'Ordinary BattleDroid red blaster only. Numeric bolt table independently matches the original PC executable.',
    'Caller supplies current posed weapon locator, targeting, character collision eligibility and terrain sweep.',
    'Native 0.2s terrain lookahead/debris/audio/shadows, cheats and shields are not reproduced here; deflection needs the explicit interception adapter.',
    'No moving-target interception or randomized target-height adjustment without caller-provided aim position.'
  ] };
  let cursor = 0, serial = 0, disposed = false;
  const end = (bolt, reason) => { if (!bolt.active) return; bolt.active = false; onEnd(bolt, reason); };
  const block = (actor, reason) => { report.blocked.push({ actor: actor?.id ?? actor?.name ?? null, reason }); return { accepted: false, reason }; };
  function allocate(owner, origin, direction, request, flags = 0) {
    let attempts = 0;
    while (slots[cursor]?.active && (slots[cursor].flags & 8) && attempts++ < 32) cursor = (cursor + 1) % 32;
    const old = slots[cursor]; if (old?.active) end(old, 'pool-replaced');
    const bolt = { ...ORDINARY_RED_BOLT, id: ++serial, slot: cursor, active: true, owner,
      position: origin.slice(), previousPosition: origin.slice(), direction: direction.slice(), velocity: direction.map(v => v * ORDINARY_RED_BOLT.speed),
      time: 0, flags, request, visualScale: [1, 0, 1] };
    slots[cursor] = bolt; cursor = (cursor + 1) % 32; onSpawn(bolt); return bolt;
  }
  function requestFire(actor, target, request = {}) {
    if (disposed || actor?.dead || target?.dead) return { accepted: false, reason: 'Actor unavailable' };
    const values = actor?.config?.values;
    if (!values?.blaster || values.variant !== 'battledroid') return block(actor, 'Unsupported original weapon/creature type');
    const action = actor.config.actions?.get('shoot'), clip = action && actor.animations?.get(action.clip);
    if (!action || !clip || !Number.isFinite(clip.firstFrame) || !Number.isFinite(clip.endFrame) || !Number.isFinite(action.playbackRate) || action.playbackRate <= 0)
      return block(actor, 'Missing original shoot action/ANI4 clip');
    if (action.eventFrames.some(frame => frame > 1)) return block(actor, 'Delayed/directional/multishot firing events unsupported');
    if (shots.has(actor)) return { accepted: false, reason: 'Original shooting animation still active' };
    const locator = values.weapon_shoot_locator ?? values.weapon_shoot_locator_1 ?? values.weapon_locator ?? values.weapon_locator_1;
    if (!Number.isInteger(locator) || locator < 0 || locator > 15) return block(actor, 'Original weapon locator unavailable');
    // Source ordinary SHOOT without event frame>1 emits immediately. Set its
    // frame before asking the renderer for the current posed locator.
    actor.aiFireClip = action.clip; actor.aiFrame = clip.firstFrame;
    const origin = getShootOrigin(actor, locator), aim = getAimPosition(actor, target);
    if (!validVector(origin) || !validVector(aim)) { delete actor.aiFireClip; delete actor.aiFrame; return block(actor, 'Original muzzle/aim position unavailable'); }
    let direction;
    try { direction = sourceBoltDirection(origin, aim); }
    catch (error) { delete actor.aiFireClip; delete actor.aiFrame; return block(actor, error.message); }
    const bolt = allocate(actor, origin, direction, request); bolt.target = target;
    shots.set(actor, { action, clip, elapsed: 0 });
    return { accepted: true, bolt };
  }
  function tick(dt) {
    if (disposed) return;
    if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Invalid original projectile timestep');
    for (const [actor, shot] of shots) {
      shot.elapsed += dt;
      actor.aiFrame = Math.min(shot.clip.endFrame, shot.clip.firstFrame + shot.elapsed * shot.action.playbackRate);
      if (actor.dead || actor.aiFrame >= shot.clip.endFrame) { shots.delete(actor); delete actor.aiFireClip; delete actor.aiFrame; }
    }
    for (const bolt of slots) {
      if (!bolt?.active) continue;
      const next = bolt.position.map((v, i) => v + bolt.velocity[i] * dt);
      const terrain = terrainSweep?.(bolt.position.slice(), next, bolt.collisionRadius, bolt);
      if (terrain === undefined) { if (!bolt.blocked) report.blocked.push({ bolt: bolt.id, reason: 'Original terrain collision unavailable' }); bolt.blocked = true; continue; }
      if (terrain !== null && (!Number.isFinite(terrain.fraction) || terrain.fraction < 0 || terrain.fraction > 1)) throw new Error('Invalid terrain collision fraction');
      // Native previous_position is initialized at spawn and retained for
      // reflection targeting; it is not the preceding integration position.
      bolt.time += dt;
      bolt.position = terrain === null ? next : bolt.position.map((v, i) => v + (next[i] - v) * terrain.fraction);
      bolt.visualScale = [1, Math.min(1, bolt.time / bolt.growTime), 1];
      const points = [2, 0, -2].map(offset => bolt.position.map((v, i) => v + bolt.direction[i] * offset * bolt.collisionRadius));
      let hit = false;
      for (const target of getTargets()) {
        if (target === bolt.owner || target.dead) continue;
        const collision = getCollision(target, bolt);
        if (!collision || collision.eligible !== true) continue;
        const pointIndex = points.findIndex(point => sourceBoltOverlap(collision, point, bolt.collisionRadius));
        if (pointIndex < 0) continue;
        const event = { bolt, point: points[pointIndex], pointIndex: 2 - pointIndex };
        const interception = interceptCharacterHit(target, bolt.damage, event);
        if (interception?.handled === true) {
          if (interception.mode === 'reflect') {
            const reflected = interception.reflected;
            if (!reflected || !validVector(reflected.position) || !validVector(reflected.direction) || reflected.owner !== target)
              throw new Error('Invalid original bolt reflection');
            allocate(reflected.owner, reflected.position, reflected.direction, { reflectedFrom: bolt.id }, reflected.flags ?? 0x10000000);
          } else if (interception.mode !== 'absorb') throw new Error('Invalid original bolt interception mode');
          end(bolt, `character-${interception.mode}`);
        } else { onDamage(target, bolt.damage, event); end(bolt, 'character'); }
        hit = true; break;
      }
      if (!hit && (terrain !== null || bolt.time >= bolt.lifetime)) end(bolt, terrain !== null ? 'terrain' : 'expired');
    }
  }
  return { requestFire, tick, slots, shots, report, get activeBolts() { return slots.filter(bolt => bolt?.active); },
    dispose() { for (const bolt of slots) if (bolt?.active) end(bolt, 'disposed'); for (const actor of shots.keys()) { delete actor.aiFireClip; delete actor.aiFrame; } shots.clear(); disposed = true; } };
}
